import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import styled, { useTheme } from "styled-components";

import {
  useCheckDailyTodo,
  useCheckDailyTodoAll,
  useUpdateRestGauge,
} from "@core/hooks/mutations/todo";
import { useCustomTodos } from "@core/hooks/queries/todo";
import useModalState from "@core/hooks/useModalState";
import { updateCharacterQueryData } from "@core/lib/queryClient";
import { LOCAL_STORAGE_KEYS, INITIAL_DAILY_TODO_ORDER } from "@core/constants";
import type { Character } from "@core/types/character";
import type { Friend } from "@core/types/friend";
import type { CustomTodoItem, DailyTodoItem } from "@core/types/todo.d";
import { getLocalStorage } from "@core/utils/localStorage";

import BoxTitle from "@components/BoxTitle";
import Modal from "@components/Modal";

import EditIcon from "@assets/svg/EditIcon";
import MoreDetailIcon from "@assets/svg/MoreDetailIcon";

import Check, * as CheckStyledComponents from "./element/Check";
import CustomContents from "./element/CustomContents";
import RestGauge, * as RestGaugeStyledComponents from "./element/RestGauge";
import GoldText from "./text/GoldText";

const DAILY_ACCORDION_KEY = "daily-accordion-state";
const WEEKLY_ACCORDION_KEY = "weekly-accordion-state";
const RAID_ACCORDION_KEY = "raid-accordion-state";
const ACCORDION_RESET_KEY = "accordion-last-reset";

// 수요일 10시 자동 펼침: 마지막 리셋 이후 수요일 10시가 지났으면 접힘 상태 초기화
const checkAndResetAccordion = () => {
  try {
    const now = new Date();
    const lastReset = localStorage.getItem(ACCORDION_RESET_KEY);
    const lastResetDate = lastReset ? new Date(lastReset) : new Date(0);

    // 이번 주 수요일 10시 계산 (KST)
    const wednesday = new Date(now);
    const dayOfWeek = wednesday.getDay();
    const diff = dayOfWeek >= 3 ? dayOfWeek - 3 : dayOfWeek + 4;
    wednesday.setDate(wednesday.getDate() - diff);
    wednesday.setHours(10, 0, 0, 0);

    // 수요일 10시가 지났고, 마지막 리셋이 그 전이면 리셋
    if (now >= wednesday && lastResetDate < wednesday) {
      localStorage.removeItem(DAILY_ACCORDION_KEY);
      localStorage.removeItem(WEEKLY_ACCORDION_KEY);
      localStorage.removeItem(RAID_ACCORDION_KEY);
      // 메모 상태는 리셋하지 않음
      localStorage.setItem(ACCORDION_RESET_KEY, now.toISOString());
    }
  } catch {
    // ignore
  }
};

// 컴포넌트 로드 시 한 번 체크
checkAndResetAccordion();

const getStoredDailyExpanded = (characterId: number): boolean => {
  try {
    const stored = localStorage.getItem(DAILY_ACCORDION_KEY);
    if (stored) {
      const map: Record<string, boolean> = JSON.parse(stored);
      return map[characterId] ?? true;
    }
  } catch {
    // ignore
  }
  return true;
};

const setStoredDailyExpanded = (characterId: number, value: boolean) => {
  try {
    const stored = localStorage.getItem(DAILY_ACCORDION_KEY);
    const map: Record<string, boolean> = stored ? JSON.parse(stored) : {};
    map[characterId] = value;
    localStorage.setItem(DAILY_ACCORDION_KEY, JSON.stringify(map));
  } catch {
    // ignore
  }
};

interface Props {
  character: Character;
  friend?: Friend;
}

const DailyContents = ({ character, friend }: Props) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(() =>
    getStoredDailyExpanded(character.characterId)
  );
  const [modalState, setModalState] = useModalState<string>();
  const [addCustomTodoMode, setAddCustomTodoMode] = useState(false);

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => {
      const next = !prev;
      setStoredDailyExpanded(character.characterId, next);
      return next;
    });
  }, [character.characterId]);

  const { data: customTodos } = useCustomTodos(friend?.friendUsername);
  const characterDailyCustomTodos = customTodos?.filter(
    (todo) =>
      todo.characterId === character.characterId && todo.frequency === "DAILY"
  );



  const checkDailyTodo = useCheckDailyTodo({
    onSuccess: (character, { friendUsername }) => {
      updateCharacterQueryData({
        character,
        friendUsername,
      });
    },
  });

  const checkDailyTodoAll = useCheckDailyTodoAll({
    onSuccess: (character, { friendUsername }) => {
      updateCharacterQueryData({
        character,
        friendUsername,
      });
    },
  });

  const updateRestGauge = useUpdateRestGauge({
    onSuccess: (character, { friendUsername }) => {
      updateCharacterQueryData({
        character,
        friendUsername,
      });
    },
  });

  const requestRestGauge = (
    gaugeType: "chaosGauge" | "guardianGauge"
  ): number | null => {
    const maxValue = gaugeType === "chaosGauge" ? 200 : 100;
    const input = window.prompt(`휴식게이지 수정`);
    if (input !== null) {
      const newNumber = Number(input);
      if (!Number.isNaN(newNumber)) {
        if (newNumber >= 0 && newNumber <= maxValue) {
          if (newNumber % 10 === 0) {
            return Number(input);
          }

          toast.error("10 단위의 숫자만 입력이 가능합니다.");
          return null;
        }

        toast.error(`0에서 ${maxValue}까지의 숫자만 입력이 가능합니다.`);
        return null;
      }

      toast.error("숫자만 입력해주세요.");
      return null;
    }

    toast.error("휴식게이지를 입력해주세요.");
    return null;
  };

  // 캐릭터 휴식게이지 업데이트
  const handleUpdateRestGauge = (gaugeType: "chaosGauge" | "guardianGauge") => {
    const newNumber = requestRestGauge(gaugeType);
    if (newNumber !== null) {
      updateRestGauge.mutate({
        friendUsername: friend?.friendUsername,
        characterId: character.characterId,
        chaosGauge: character.chaosGauge,
        guardianGauge: character.guardianGauge,
        [gaugeType]: newNumber,
      });
    }
  };

  // 깐부의 캐릭터라면 나에게 설정한 값도 체크해야 함
  const accessible = friend ? friend.fromFriendSettings.showDayTodo : true;

  const getDailyTodoTotalCount = (
    character: Character,
    customDayTodos?: CustomTodoItem[]
  ) => {
    const { showChaos, showGuardian } = character.settings;
    let count = 0;

    if (showChaos) count += 2;
    if (showGuardian) count += 1;

    if (customDayTodos) {
      count += customDayTodos.length;
    }

    return count;
  };

  const getDailyTodoCheck = (
    character: Character,
    customDayTodos?: CustomTodoItem[]
  ) => {
    const { showChaos, showGuardian } = character.settings;

    let count = 0;

    if (showChaos) count += character.chaosCheck;
    if (showGuardian) count += character.guardianCheck;

    if (customDayTodos) {
      count += customDayTodos.filter((item) => item.checked).length;
    }

    return count;
  };

  return (
    <>
      <Wrapper>
        <TitleRow>
          {accessible && (
            <Check
              totalCount={getDailyTodoTotalCount(
                character,
                characterDailyCustomTodos
              )}
              currentCount={getDailyTodoCheck(
                character,
                characterDailyCustomTodos
              )}
              onClick={() => {
                checkDailyTodoAll.mutate({
                  friendUsername: friend?.friendUsername,
                  characterId: character.characterId,
                });
              }}
              onRightClick={() => {
                checkDailyTodoAll.mutate({
                  friendUsername: friend?.friendUsername,
                  characterId: character.characterId,
                });
              }}
              rightButtons={[
                {
                  ariaLabel: "커스텀 투두 추가",
                  onClick: () => setAddCustomTodoMode(true),
                  icon: <EditIcon />,
                },
                {
                  ariaLabel: "일일 숙제 접기/펼치기",
                  onClick: toggleExpanded,
                  icon: <ToggleArrow $expanded={expanded}>▼</ToggleArrow>,
                },
              ]}
            >
              <BoxTitle>일일 숙제</BoxTitle>
            </Check>
          )}
        </TitleRow>

        <CollapsiblePanel $expanded={expanded}>
        <CollapsibleInner>
        {accessible &&
          (getLocalStorage<DailyTodoItem[]>(LOCAL_STORAGE_KEYS.dailyTodoOrder) || INITIAL_DAILY_TODO_ORDER).map((item) => {
            if (item.id === "chaos" && character.settings.showChaos && character.beforeChaosGauge >= character.settings.thresholdChaos) {
              return (
                <TodoWrap key={item.id} $currentCount={character.chaosCheck} $totalCount={2}>
                  <Check
                    indicatorColor={theme.app.palette.blue[350]}
                    totalCount={2}
                    currentCount={character.chaosCheck}
                    onClick={() => {
                      checkDailyTodo.mutate({
                        friendUsername: friend?.friendUsername,
                        characterId: character.characterId,
                        category: "chaos",
                        allCheck: true,
                      });
                    }}
                    onRightClick={() => {
                      checkDailyTodo.mutate({
                        friendUsername: friend?.friendUsername,
                        characterId: character.characterId,
                        category: "chaos",
                        allCheck: true,
                      });
                    }}
                    rightButtons={[
                      {
                        ariaLabel: "카오스던전 보상 확인하기",
                        onClick: () => setModalState("카오스던전"),
                        icon: <MoreDetailIcon />,
                      },
                    ]}
                  >
                    {character.chaos?.name || "카오스던전"}
                  </Check>
                  <RestGauge
                    totalValue={200}
                    currentValue={character.chaosGauge}
                    onClick={() => handleUpdateRestGauge("chaosGauge")}
                  />
                </TodoWrap>
              );
            } if (item.id === "guardian" && character.settings.showGuardian && character.beforeGuardianGauge >= character.settings.thresholdGuardian) {
              return (
                <TodoWrap key={item.id} $currentCount={character.guardianCheck} $totalCount={1}>
                  <Check
                    indicatorColor={theme.app.palette.blue[350]}
                    totalCount={1}
                    currentCount={character.guardianCheck}
                    onClick={() => {
                      checkDailyTodo.mutate({
                        friendUsername: friend?.friendUsername,
                        characterId: character.characterId,
                        category: "guardian",
                        allCheck: false,
                      });
                    }}
                    onRightClick={() => {
                      checkDailyTodo.mutate({
                        friendUsername: friend?.friendUsername,
                        characterId: character.characterId,
                        category: "guardian",
                        allCheck: true,
                      });
                    }}
                    rightButtons={[
                      {
                        ariaLabel: "가디언토벌 보상 확인하기",
                        onClick: () => setModalState("가디언토벌"),
                        icon: <MoreDetailIcon />,
                      },
                    ]}
                  >
                    <ContentNameWithGold>
                      {character.guardian?.name || "가디언토벌"}
                      {character.guardianGold > 0 && (
                        <GoldText>{character.guardianGold.toFixed(2)}</GoldText>
                      )}
                    </ContentNameWithGold>
                  </Check>
                  <RestGauge
                    totalValue={100}
                    currentValue={character.guardianGauge}
                    onClick={() => handleUpdateRestGauge("guardianGauge")}
                  />
                </TodoWrap>
              );
            } if (item.id === "custom") {
              return (
                <CustomContents
                  key={item.id}
                  setAddMode={setAddCustomTodoMode}
                  addMode={addCustomTodoMode}
                  character={character}
                  friend={friend}
                  frequency="DAILY"
                />
              );
            }
            return null;
          })}
        </CollapsibleInner>
        </CollapsiblePanel>
      </Wrapper>

      {modalState && (
        <Modal
          title={`${character.characterName} ${modalState} 평균 데이터`}
          isOpen
          onClose={() => setModalState()}
        >
          <ModalContentWrapper>
            <ModalSubTitle>
              API 최근 경매장 가격으로 평균 값을 가져옵니다.
            </ModalSubTitle>

            {modalState === "카오스던전" ? (
              <>
                <ContentName>컨텐츠 {character.chaos.name}</ContentName>

                <ProfitList>
                  <li>
                    <Profit>
                      <dt>거래 가능 재화</dt>
                      <dd>
                        파괴석
                        <strong>{character.chaos.destructionStone}개</strong>
                      </dd>
                      <dd>
                        수호석
                        <strong>{character.chaos.guardianStone}개</strong>
                      </dd>
                      <dd>
                        1레벨보석 <strong>{character.chaos.jewelry}개</strong>
                      </dd>
                    </Profit>
                  </li>
                  <li>
                    <Profit>
                      <dt>거래 불가 재화</dt>
                      <dd>
                        돌파석 <strong>{character.chaos.leapStone}개</strong>
                      </dd>
                      <dd>
                        실링 <strong>{character.chaos.shilling}개</strong>
                      </dd>
                      <dd>
                        파편 <strong>{character.chaos.honorShard}개</strong>
                      </dd>
                    </Profit>
                  </li>
                </ProfitList>
              </>
            ) : (
              <>
                <ContentName>컨텐츠 {character.guardian.name}</ContentName>

                <ProfitList>
                  <li>
                    <Profit>
                      <dt>거래 가능 재화</dt>
                      <dd>
                        파괴석{" "}
                        <strong>{character.guardian.destructionStone}개</strong>
                      </dd>
                      <dd>
                        수호석{" "}
                        <strong>{character.guardian.guardianStone}개</strong>
                      </dd>
                      <dd>
                        돌파석 <strong>{character.guardian.leapStone}개</strong>
                      </dd>
                    </Profit>
                  </li>
                </ProfitList>
              </>
            )}
          </ModalContentWrapper>
        </Modal>
      )}
    </>
  );
};

export default DailyContents;

const Wrapper = styled.div`
  width: 100%;
  background: ${({ theme }) => theme.app.bg.white};

  ${CheckStyledComponents.Wrapper}, ${RestGaugeStyledComponents.Wrapper} {
    border-top: 1px solid ${({ theme }) => theme.app.border};
  }
`;

const TitleRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0px;
  color: ${({ theme }) => theme.app.text.black};
`;

const ToggleArrow = styled.span<{ $expanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 6px;
  font-size: 10px;
  color: ${({ theme }) => theme.app.text.light2};
  transition: transform 0.2s ease;
  transform: rotate(${({ $expanded }) => ($expanded ? "180deg" : "0deg")});
`;

const CollapsiblePanel = styled.div<{ $expanded: boolean }>`
  display: grid;
  grid-template-rows: ${({ $expanded }) => ($expanded ? "1fr" : "0fr")};
  transition: grid-template-rows 0.25s ease;
`;

const CollapsibleInner = styled.div`
  overflow: hidden;
  min-height: 0;
`;

const TodoWrap = styled.div<{
  $currentCount: number;
  $totalCount: number;
}>`
  opacity: ${(props) => (props.$currentCount === props.$totalCount ? 0.5 : 1)};
`;

const ContentNameWithGold = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1;
  gap: 2px;
`;

const ModalContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ModalSubTitle = styled.p`
  margin-bottom: 10px;
  font-size: 14px;
  color: ${({ theme }) => theme.app.text.light2};
`;

const ContentName = styled.p`
  color: ${({ theme }) => theme.app.palette.smokeBlue[500]};
  font-size: 16px;
`;

const ProfitList = styled.ul`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  margin-top: 10px;

  ${({ theme }) => theme.medias.max500} {
    flex-direction: column;
  }

  li {
    width: 160px;

    &:not(:last-of-type) {
      padding-right: 20px;

      ${({ theme }) => theme.medias.max500} {
        padding-right: 0;
        padding-bottom: 20px;
      }
    }
  }

  li + li {
    padding-left: 20px;
    border-left: 1px solid ${({ theme }) => theme.app.border};

    ${({ theme }) => theme.medias.max500} {
      padding-left: 0;
      padding-top: 20px;
      border-left: none;
      border-top: 1px solid ${({ theme }) => theme.app.border};
    }
  }
`;

const Profit = styled.dl`
  dt {
    text-align: center;
  }

  dd {
    color: ${({ theme }) => theme.app.text.light1};

    strong {
      margin-left: 5px;
      color: ${({ theme }) => theme.app.text.black};
      font-weight: 700;
    }
  }
`;
